import type { ActivityNavigationItem } from '_components/ActivityNavigationGrid/types';

/**
 * The subset of an `activity_navigation_grid` slice this module relies on. Like
 * `image_hotspot`, this is an embedded *slice* on `category_page`/`landing_page`
 * (Prismic's `{ primary, items }` slice shape) rather than a standalone
 * document, so there's no `data` wrapper here.
 */
export interface ActivityNavigationGridItem {
  label?: string | null;
  url?: string | null;
  image?: { url?: string | null; alt?: string | null } | null;
  cta_label?: string | null;
}

export interface ActivityNavigationGridSlice {
  id?: string;
  slice_type?: string;
  primary?: {
    heading?: string | null;
    body?: string | null;
  } | null;
  items?: ActivityNavigationGridItem[] | null;
}

export interface ActivityNavigationGridData {
  heading: string;
  body?: string;
  items: ActivityNavigationItem[];
}

const trimmed = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null;

/**
 * Prismic authors enter a relative path or an absolute URL, never a
 * locale-prefixed one — matches `PlpCrossLinking`'s `buildCtaHref` and the
 * project rule for locale-prefixed links (`/${locale ?? 'en'}${path}`).
 */
const buildHref = (raw: string | null, locale?: string): string | null => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `/${locale ?? 'en'}${path}`;
};

/**
 * Maps an `activity_navigation_grid` slice to `ActivityNavigationGrid` props.
 * An item missing a label, a resolvable url, or an image is dropped rather
 * than rendered broken. Returns `null` when the heading is blank or no item
 * survives, so the page never renders an empty component (the component
 * itself also guards on `items.length`, but the transform stays consistent
 * with `image_hotspot`'s null-on-unusable contract).
 */
export const transformActivityNavigationGrid = (
  slice: ActivityNavigationGridSlice | null,
  locale?: string
): ActivityNavigationGridData | null => {
  const heading = trimmed(slice?.primary?.heading);
  if (!heading) {
    return null;
  }

  const items: ActivityNavigationItem[] = (slice?.items ?? []).flatMap(
    (item) => {
      const label = trimmed(item?.label);
      const href = buildHref(trimmed(item?.url), locale);
      const imageUrl = trimmed(item?.image?.url);

      if (!label || !href || !imageUrl) {
        return [];
      }

      return [
        {
          label,
          href,
          image: { url: imageUrl, alt: trimmed(item?.image?.alt) ?? '' },
          ctaLabel: trimmed(item?.cta_label) ?? undefined,
        },
      ];
    }
  );

  if (items.length === 0) {
    return null;
  }

  return {
    heading,
    body: trimmed(slice?.primary?.body) ?? undefined,
    items,
  };
};

export default transformActivityNavigationGrid;
